import { Injectable } from '@angular/core';
import { AWS_COGNITO_REGION_KEY, COGNITO_REGIONS_MAPPING, SUPPORTED_LANGUAGES, USER_COUNTRY_KEY } from '@app-core/constants/constants';
import { CognitoRegionType } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { CLIENT_CONFIG } from '@config/config';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class LoginPageUtilService {
  public config = CLIENT_CONFIG;
  public showRegionSelection =
    Boolean(CLIENT_CONFIG?.landingPageDetails?.defaultUserCountry) || Object.keys(environment?.cognitoConfigs || {})?.length > 1;
  public regions: CognitoRegionType[] = [];
  public supportedLanguages = SUPPORTED_LANGUAGES;

  constructor(private storageService: StorageService, private accessService: AccessService, private translate: TranslateService) {}

  public getRegionValue() {
    const { allowedRegions = [], defaultUserCountry } = this.config?.landingPageDetails || {};
    this.regions = Object.keys(environment?.cognitoConfigs || {})
      .reduce((a, r) => [...a, ...(COGNITO_REGIONS_MAPPING[r] || [])], [])
      .sort((a, b) => {
        if (a.value === 'OTHER') return 1;
        if (b.value === 'OTHER') return -1;
        return a.label.localeCompare(b.label);
      });

    if (allowedRegions.length) {
      this.regions = this.regions.filter((region) => allowedRegions.includes(region.value));
    }

    const previouslySelectedRegion =
      defaultUserCountry ||
      (this.showRegionSelection ? this.storageService.getStorageValue(USER_COUNTRY_KEY) : (this.regions[0] || {}).value);

    return {
      regions: this.regions,
      previouslySelectedRegion: previouslySelectedRegion || '',
    };
  }

  public regionChanged(selectedRegion) {
    const awsCognitoRegion = this.getAwsCognitoRegion(selectedRegion);
    this.storageService.setStorageValue(USER_COUNTRY_KEY, selectedRegion);
    if (typeof awsCognitoRegion === 'string' && awsCognitoRegion.trim() !== '') {
      this.storageService.setStorageValue(AWS_COGNITO_REGION_KEY, awsCognitoRegion);
    }
  }

  private getAwsCognitoRegion(userCountry: string): string {
    const cognitoRegionVsCountries = Object.entries(COGNITO_REGIONS_MAPPING).find(([, countries]) =>
      countries.find((c) => c.value === userCountry)
    );
    return (cognitoRegionVsCountries || [])[0];
  }

  public getCurrentLanguage() {
    const prefferedLanguage = this.accessService.landingPageLanguage || navigator?.language;
    const langSupported = this.supportedLanguages.find((lang) => lang.key === prefferedLanguage);
    const currentLanguage = this.config?.showCustomLoginPage && langSupported ? prefferedLanguage : 'en';

    this.accessService.landingPageLanguage = currentLanguage;
    this.translate.use(currentLanguage);

    return currentLanguage;
  }

  public languageChanged(selectedLanguage: string) {
    if (selectedLanguage) {
      this.accessService.landingPageLanguage = selectedLanguage;
      this.translate.use(selectedLanguage);
    }
  }
}
